import type { AccountPlan } from '../../global';
import { t } from '../../lib/i18n';

/**
 * Özet şablonlarının arayüz kataloğu: ad, açıklama, önizleme ve hangi planda
 * açık olduğu. Modele giden yapı yönergesi ana süreçte
 * (src/services/summaryTemplates.ts); iki taraf aynı kimlikleri kullanıyor ve
 * `npm run test:prefs` listelerin ayrışmadığını kontrol ediyor.
 *
 * Önizleme metinleri örnek içerik — gerçek özet toplantıdan üretiliyor.
 */

export type TemplateSectionKind = 'paragraph' | 'checklist' | 'outline';

export interface TemplateSection {
  title: string;
  kind: TemplateSectionKind;
  lines: string[];
}

export interface SummaryTemplate {
  id: string;
  name: string;
  description: string;
  /** Şablonun açık olduğu en düşük plan. */
  minPlan: AccountPlan;
  sections: TemplateSection[];
}

const PLAN_RANK: Record<AccountPlan, number> = { basic: 0, pro: 1, plus: 2, enterprise: 3 };

export function isTemplateUnlocked(template: SummaryTemplate, plan: AccountPlan): boolean {
  return PLAN_RANK[plan] >= PLAN_RANK[template.minPlan];
}

/** "Önerilen toplantı biçimi" düğmesinin doldurduğu kurallar. */
export function recommendedRules(): string[] {
  return [
    t('Özete toplantının amacıyla ve en önemli çıktıyla başla.', 'Open with the purpose of the meeting and the most important outcome.'),
    t('Aksiyon maddelerini kişi adıyla ve varsa tarihiyle yaz.', 'Write action items with the owner’s name and a date when there is one.'),
    t('Kararları ayrı ayrı, tek cümlelik maddeler hâlinde listele.', 'List decisions separately, one sentence each.'),
    t('Konuşulmayan bir konuyu ekleme; emin değilsen yazma.', 'Never add a topic that was not discussed; if unsure, leave it out.'),
  ];
}

export function summaryTemplates(): SummaryTemplate[] {
  return [
    {
      id: 'blank',
      name: t('Boş', 'Blank'),
      description: t('Ek yapı yok; kısa ve serbest bir özet.', 'No extra structure; a short, free-form summary.'),
      minPlan: 'basic',
      sections: [
        {
          title: t('Serbest özet', 'Free-form summary'),
          kind: 'paragraph',
          lines: [
            t(
              'Toplantının kısa bir özeti; başlık ve madde yapısı zorlanmaz.',
              'A short summary of the meeting, without forcing headings or bullets.'
            ),
          ],
        },
      ],
    },
    {
      id: 'general',
      name: t('Genel', 'General'),
      description: t('Çoğu toplantı için dengeli varsayılan.', 'A balanced default for most meetings.'),
      minPlan: 'basic',
      sections: [
        {
          title: t('Genel bakış', 'Overview'),
          kind: 'paragraph',
          lines: [
            t(
              'Ekip ürün ilerlemesini, önemli kilometre taşlarını ve önümüzdeki hedefleri gözden geçirdi.',
              'The team reviewed product progress, key milestones and the goals ahead.'
            ),
          ],
        },
        {
          title: t('Aksiyon maddeleri', 'Action items'),
          kind: 'checklist',
          lines: [
            t('Öncelikleri netleştir ve teslim tarihlerini belirle.', 'Clarify priorities and set delivery dates.'),
            t('Ekipler arası koordinasyon için bir akış kur.', 'Set up a flow for cross-team coordination.'),
          ],
        },
        {
          title: t('Ana başlıklar', 'Outline'),
          kind: 'outline',
          lines: [
            t('Ürün ilerlemesi (0:20)', 'Product progress (0:20)'),
            t('Bütçe ve büyüme (8:12)', 'Budget and growth (8:12)'),
            t('Riskler ve engeller (22:35)', 'Risks and blockers (22:35)'),
          ],
        },
      ],
    },
    {
      id: 'team-meeting',
      name: t('Ekip toplantısı', 'Team meeting'),
      description: t('Gündem, engeller ve sonraki adımlar.', 'Agenda, blockers and next steps.'),
      minPlan: 'basic',
      sections: [
        {
          title: t('Gündem', 'Agenda'),
          kind: 'outline',
          lines: [
            t('Geçen haftanın özeti', 'Recap of last week'),
            t('Devam eden işler', 'Work in progress'),
          ],
        },
        {
          title: t('Engeller', 'Blockers'),
          kind: 'paragraph',
          lines: [
            t('Tasarım onayı beklendiği için iki iş duraklatıldı.', 'Two items are paused while design approval is pending.'),
          ],
        },
        {
          title: t('Sonraki adımlar', 'Next steps'),
          kind: 'checklist',
          lines: [t('Onay için tasarımla görüş.', 'Follow up with design for approval.')],
        },
      ],
    },
    {
      id: 'one-on-one',
      name: t('Bire bir görüşme', 'One-on-one'),
      description: t('Konuşulanlar, geri bildirim ve takip.', 'Topics, feedback and follow-ups.'),
      minPlan: 'basic',
      sections: [
        {
          title: t('Konuşulanlar', 'Topics'),
          kind: 'paragraph',
          lines: [
            t('İş yükü, öncelikler ve kariyer hedefleri konuşuldu.', 'Workload, priorities and career goals were discussed.'),
          ],
        },
        {
          title: t('Geri bildirim', 'Feedback'),
          kind: 'outline',
          lines: [
            t('İyi giden: ekip içi iletişim', 'Going well: communication within the team'),
            t('Geliştirilecek: tahmin süreleri', 'To improve: estimates'),
          ],
        },
        {
          title: t('Takip', 'Follow-ups'),
          kind: 'checklist',
          lines: [t('Önümüzdeki ay için iki hedef belirle.', 'Set two goals for next month.')],
        },
      ],
    },
    {
      id: 'interview',
      name: t('Aday görüşmesi', 'Candidate interview'),
      description: t('Deneyim, güçlü yönler ve karar.', 'Experience, strengths and the decision.'),
      minPlan: 'basic',
      sections: [
        {
          title: t('Aday özeti', 'Candidate summary'),
          kind: 'paragraph',
          lines: [
            t('Beş yıllık deneyim; benzer bir üründe ekip liderliği yapmış.', 'Five years of experience; led a team on a similar product.'),
          ],
        },
        {
          title: t('Güçlü yönler', 'Strengths'),
          kind: 'outline',
          lines: [t('Teknik derinlik', 'Technical depth'), t('Net iletişim', 'Clear communication')],
        },
        {
          title: t('Sonraki adım', 'Next step'),
          kind: 'checklist',
          lines: [t('Teknik değerlendirme planla.', 'Schedule the technical round.')],
        },
      ],
    },
    {
      id: 'sales-bant',
      name: t('Satış (BANT)', 'Sales — BANT'),
      description: t('Bütçe, yetki, ihtiyaç ve zaman çizelgesi.', 'Budget, authority, need and timeline.'),
      minPlan: 'pro',
      sections: [
        {
          title: 'BANT',
          kind: 'outline',
          lines: [
            t('Bütçe: yıllık bütçe ayrılmış', 'Budget: an annual budget is allocated'),
            t('Yetki: kararı ürün müdürü veriyor', 'Authority: the product manager decides'),
            t('İhtiyaç: toplantı notlarının otomatikleşmesi', 'Need: automating meeting notes'),
            t('Zaman: bu çeyrek', 'Timeline: this quarter'),
          ],
        },
        {
          title: t('İtirazlar', 'Objections'),
          kind: 'outline',
          lines: [t('Veri güvenliği soruları', 'Questions about data security')],
        },
        {
          title: t('Sonraki adım', 'Next step'),
          kind: 'checklist',
          lines: [t('Güvenlik dokümanını gönder.', 'Send the security document.')],
        },
      ],
    },
    {
      id: 'customer-success',
      name: t('Müşteri görüşmesi', 'Customer success'),
      description: t('Durum, talepler, riskler ve takip.', 'Status, requests, risks and follow-ups.'),
      minPlan: 'pro',
      sections: [
        {
          title: t('Durum', 'Status'),
          kind: 'paragraph',
          lines: [t('Kullanım artıyor; ekip yeni modülü deniyor.', 'Usage is growing; the team is trying the new module.')],
        },
        {
          title: t('Talepler ve riskler', 'Requests and risks'),
          kind: 'outline',
          lines: [
            t('Talep: toplu dışa aktarım', 'Request: bulk export'),
            t('Risk: sözleşme yenilemesi yaklaşıyor', 'Risk: renewal is approaching'),
          ],
        },
        {
          title: t('Takip', 'Follow-ups'),
          kind: 'checklist',
          lines: [t('Yenileme görüşmesi planla.', 'Schedule the renewal conversation.')],
        },
      ],
    },
    {
      id: 'user-research',
      name: t('Kullanıcı araştırması', 'User research'),
      description: t('Bağlam, acı noktaları ve çıkarımlar.', 'Context, pain points and takeaways.'),
      minPlan: 'pro',
      sections: [
        {
          title: t('Bağlam', 'Context'),
          kind: 'paragraph',
          lines: [t('Haftada üç toplantı yapan küçük bir ekip.', 'A small team with three meetings a week.')],
        },
        {
          title: t('Acı noktaları', 'Pain points'),
          kind: 'outline',
          lines: [
            t('Notlar dağınık kalıyor', 'Notes stay scattered'),
            t('Aksiyonlar takip edilemiyor', 'Action items are hard to track'),
          ],
        },
        {
          title: t('Çıkarımlar', 'Takeaways'),
          kind: 'checklist',
          lines: [t('Aksiyon takibini öne çıkar.', 'Put action tracking front and center.')],
        },
      ],
    },
  ];
}

export function templateById(id: string): SummaryTemplate | undefined {
  return summaryTemplates().find((template) => template.id === id);
}
